import React, {
  useRef, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getConfig } from '@edx/frontend-platform';
import { useModel } from '@src/generic/model-store';
import { fetchCourse } from '@src/courseware/data';
import { processEvent } from '@src/course-home/data/thunks';

/**
 * IGEA: Main content area — pixel-perfect white card with shadow.
 * Renders the XBlock iframe. Injects CSS into iframe document to hide
 * edX-generated chrome (unit title h2, bookmark button).
 */

// CSS injected into the XBlock iframe.
//
// Goal: strip edX/Studio's own chrome (unit title, bookmark button, default
// surrounding paddings/borders) so the IGEA Video player sits flush inside
// the white card we render in IgeaMainContent. We do NOT restyle the tab
// strip here — the XBlock owns its own tab CSS and overriding it from this
// side caused conflicts (border-radius, active-tab fusion with panel, etc.).
const IFRAME_INJECT_CSS = `
  /* Hide edX unit title + bookmark — duplicates of what we already show */
  h2.hd.hd-2.unit-title,
  .unit-title,
  .bookmark-button-wrapper,
  button.bookmark-button { display: none !important; }

  /* Transparent body so the white card behind the iframe shows through */
  body, html {
    background: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  /* Strip default xblock wrapper padding/borders so our card edge is
     the only visible boundary */
  .xblock, .xblock-student_view, .xblock-student_view-vertical,
  .vert-mod, .vert {
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    background: transparent !important;
  }
`;

// Quanto si aspetta l'annuncio dell'unita' prima di decidere da soli. Lo script
// iniettato annuncia appena l'HTML e' stato analizzato, quindi tre secondi sono
// gia' molto piu' del necessario: coprono un iframe lento, non un iframe rotto.
const ATTESA_VINCOLO_MS = 3000;

// `attesa`: l'unita' non ha ancora parlato. `muta`: non ha parlato entro
// l'attesa. `noto`: ha detto la sua.
const GATE_INIZIALE = {
  stato: 'attesa',
  vincolato: false,
  aperto: false,
  secondiMancanti: 0,
  tipo: '',
  percorsoVincolato: false,
};

// Collaudo P-12 (learning #7): fuori dall'ECM l'unita' annuncia
// `igea:advance-timeout` quando il server non conferma il completamento — di
// solito perche' il video e' stato saltato fino alla fine, che non vale come
// visione. Senza questa riga l'utente restava sulla lezione senza un perche'.
export const AVVISO_AVANZAMENTO_MANCATO = 'La lezione non risulta ancora completata, '
  + 'quindi non siamo passati alla successiva. Puoi proseguire con «Lezione successiva».';

// Forma dell'identificativo di un allegato: un ULID, 26 caratteri in base32
// di Crockford (niente I, L, O, U). E' la stessa forma che il backend
// pretende (`Str::isUlid` in `MaterialeDidatticoController`), controllata
// qui perche' quel valore arriva dal contenuto della lezione e finisce in un
// percorso: si verifica prima di comporlo, non si spera che il server
// rifiuti. Un identificativo di un'altra forma (le voci dei corsi importati
// prima di DEC-064 portavano il nome del file) non ha nessun link da
// chiedere.
const FORMA_ALLEGATO = /^[0-9A-HJKMNP-TV-Z]{26}$/;

const IgeaMainContent = ({
  courseId, sequenceId, unitId, propedeuticita, sequenceIds,
}) => {
  const iframeRef = useRef(null);
  const unit = useModel('units', unitId);
  // La guardia sta su `unitId` e non solo sul modello: durante il passaggio da
  // una lezione all'altra l'id è momentaneamente nullo mentre il modello e'
  // ancora quello di prima, e l'iframe finiva su /xblock/null — una pagina di
  // errore completa di footer che lampeggiava prima del contenuto giusto.
  const dispatch = useDispatch();

  // Stessi parametri del player standard di Open edX. Senza `view=student_view`
  // l'unità viene resa in una vista che non è quella prevista dal courseware.
  const iframeUrl = unitId && unit
    ? `${getConfig().LMS_BASE_URL}/xblock/${unitId}?show_title=0&show_bookmark=0&recheck_access=1&view=student_view`
    : null;

  // Eventi che l'unità manda al player (segnalibri, apertura di una sequenza),
  // come fa `useIFrameBehavior` nel player standard che il nostro layout non usa.
  //
  // ATTENZIONE, qui c'era un equivoco che è costato un giro a vuoto: NON è di
  // qui che passa la pubblicazione del completamento. L'iframe dell'unità è
  // servito da `lms.*`, quindi è same-origin con l'LMS e la POST a
  // `publish_completion` la fa da sé — la fa lo script iniettato da
  // `plugins/igea/unit_gate.py` in igea-edx. Il player non deve fare da tramite,
  // e infatti farglielo fare non aveva risolto nulla (commit 8432a05).
  useEffect(() => {
    const inoltra = (e) => {
      if (e?.data?.event_name) {
        dispatch(processEvent(e.data, fetchCourse));
      }
    };
    window.addEventListener('message', inoltra);
    return () => window.removeEventListener('message', inoltra);
  }, [dispatch]);

  // Prev/next si costruiscono sull'elenco ordinato delle lezioni che
  // IgeaPlayerLayout calcola dall'outline (`orderedSeqIds`, in
  // IgeaPlayerLayout.jsx) — lo stesso elenco che disegna la sidebar e che
  // guida l'auto-avanzamento. Prima veniva ricalcolato qui da
  // `sequenceIdsSelector`: legge un'altra fetta di redux (il reducer
  // "courseware" classico, popolato una volta sola al montaggio da
  // `fetchCourse` e mai piu' aggiornato) invece dell'outline, che
  // IgeaPlayerLayout richiede di nuovo a ogni cambio lezione apposta perche'
  // e' la fonte che sappiamo funzionare (e' la stessa da cui esce la
  // sidebar). Se quella prima fetch fallisce o arriva vuota, la sidebar si
  // riprende al giro dopo — la vecchia lista qui restava vuota per tutta la
  // sessione, e "Lezione successiva" non trovava mai una destinazione,
  // indipendentemente dal vincolo ECM.
  const { prevLesson, nextLesson } = useMemo(() => {
    if (!sequenceId || sequenceIds.length === 0 || !courseId) {
      return { prevLesson: null, nextLesson: null };
    }
    const idx = sequenceIds.indexOf(sequenceId);
    const prevId = idx > 0 ? sequenceIds[idx - 1] : null;
    const nextId = idx >= 0 && idx < sequenceIds.length - 1 ? sequenceIds[idx + 1] : null;
    return {
      prevLesson: prevId ? `/course/${courseId}/${prevId}` : null,
      nextLesson: nextId ? `/course/${courseId}/${nextId}` : null,
    };
  }, [courseId, sequenceId, sequenceIds]);

  // The iframe used to auto-resize to its scrollHeight via a setInterval
  // polling loop. Inside the IGEA full-screen modal the XBlock can grow
  // unboundedly (transcript list, infinite tab content, etc.), and the
  // poll happily kept extending the iframe out of the viewport. We now
  // give the iframe a fixed height bounded to the surrounding card and
  // let the iframe scroll its own content if needed — that way the
  // video and controls stay visible no matter how tall the XBlock
  // payload becomes.
  // Stato comunicato dall'interno dell'unita' (vedi plugins/igea/unit_gate.py
  // in igea-edx). Il player e l'unita' sono su origini diverse: da qui non si
  // puo' leggere il contenuto dell'iframe, quindi altezza e sblocco arrivano
  // per postMessage.
  //
  // Si parte chiusi. Prima si partiva aperti, e cosi' qualunque intoppo —
  // un'unita' senza script, un errore JS, un iframe lento — apriva il vincolo
  // invece di chiuderlo: nel percorso ECM e' esattamente il buco che il vincolo
  // dovrebbe tappare, perche' basta un guasto per superare la lezione senza
  // averla vista.
  const [gate, setGate] = useState(GATE_INIZIALE);
  const [avanzamentoMancato, setAvanzamentoMancato] = useState(false);
  const [altezzaContenuto, setAltezzaContenuto] = useState(null);

  // Segnalazione cliente (2026-08-11), sullo stesso fenomeno del footer
  // tagliato ma con la soluzione preferita: non "far scorrere" un video
  // troppo alto, ma non fargli MAI superare lo schermo disponibile — "non
  // vederlo mai tagliato".
  //
  // Il video non lo disegna questo player: e' dentro l'iframe, reso
  // dall'XBlock `igea_video` (repo igea-edx). Il player oggi si limita a
  // seguire `altezzaContenuto` (quanto l'unita' dice di essere alta) —
  // insegue il contenuto invece di dargli un limite. Perche' il video possa
  // restringersi mantenendo le proporzioni serve che SAPPIA quanto spazio ha
  // — informazione che solo il player conosce, perche' e' lui a vedere lo
  // schermo intero (header, sidebar, footer del player). Da qui il nuovo
  // messaggio `igea:content-viewport` piu' sotto: e' la meta' del lavoro che
  // spetta a questo repo. L'altra meta' — l'XBlock che usa quel numero per
  // dimensionare il video — e' fuori da qui.
  //
  // Nel frattempo, indipendentemente da cosa fa l'XBlock con l'informazione,
  // il player SMETTE di lasciar crescere l'iframe oltre lo spazio misurato:
  // e' quello che garantisce che il footer resti raggiungibile senza
  // scorrere anche se l'unita' non e' (ancora) stata aggiornata per
  // rispettarlo.
  const areaContenutoRef = useRef(null);
  const [alturaDisponibile, setAlturaDisponibile] = useState(null);
  useEffect(() => {
    const nodo = areaContenutoRef.current;
    // jsdom (i test) non implementa ResizeObserver: senza questa guardia il
    // player degraderebbe silenziosamente al comportamento precedente
    // (nessun limite noto, l'iframe insegue solo il contenuto) — corretto
    // per i test, che non hanno un layout vero da misurare, ma va marcato
    // esplicitamente per non confonderlo con un browser che semplicemente
    // non ha ancora misurato nulla.
    if (!nodo || typeof ResizeObserver === 'undefined') { return undefined; }
    const osservatore = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect?.height;
      if (Number.isFinite(box) && box > 0) {
        setAlturaDisponibile(Math.round(box));
      }
    });
    osservatore.observe(nodo);
    return () => osservatore.disconnect();
    // Una volta sola: il nodo osservato e' lo stesso attraverso tutti i
    // cambi di lezione (IgeaMainContent non si smonta cambiando unita'), e
    // ResizeObserver segue da solo ogni ridimensionamento della finestra o
    // del layout intorno — non serve reistanziarlo.
  }, []);

  // Segnala di visitatore reale: "vedo uno sfarfallio quando vado avanti e
  // indietro nelle lezioni... sembra ricarichi la pagina". Ed è letteralmente
  // quello che succede: cambiare `src` a un iframe scarica e ricostruisce un
  // documento da capo, e per un istante il riquadro torna bianco. A questo si
  // somma un secondo collasso: `unitId` passa per `null` durante il cambio
  // lezione (vedi il commento su `unit` più sopra), quindi il contenuto
  // sparisce del tutto un istante prima ancora che il nuovo iframe cominci a
  // caricare.
  //
  // Si parte "non pronti" — vale sia per il primo montaggio sia per ogni
  // cambio di lezione — e si torna pronti solo quando l'evento `load`
  // dell'iframe dice che il nuovo documento è disponibile (o quando, nel
  // ramo sotto, risulta già completo). Fino ad allora un overlay copre il
  // riquadro invece di lasciarlo bianco.
  //
  // Deliberatamente NON un secondo iframe tenuto vivo in parallelo: un iframe
  // alternato continuerebbe a ricevere `igea:unit-gate` / `igea:attivita`
  // dalla lezione precedente finché non lo si smonta esplicitamente, e quei
  // messaggi finirebbero scambiati per quelli della lezione nuova — false
  // letture sul vincolo ECM e sulla presenza. Un solo iframe, con `src` che
  // cambia, evita il problema alla radice: il browser cancella la
  // navigazione precedente nel momento in cui `src` cambia di nuovo, quindi
  // non esiste mai un documento "vecchio" ancora in ascolto.
  const [contenutoPronto, setContenutoPronto] = useState(false);

  // L'unita' ha chiesto di sapere quanto spazio ha (`igea:content-viewport-
  // richiesta`, vedi piu' sotto). E' una prova piu' forte di `contenutoPronto`:
  // quello dice che l'evento `load` dell'iframe e' scattato, questo dice che
  // dentro c'e' uno script che sta ascoltando ADESSO. Si azzera a ogni cambio
  // di unita' insieme al resto, perche' il documento e' un altro.
  const [unitaHaChiesto, setUnitaHaChiesto] = useState(false);

  // Contatore di rimontaggio manuale: e' l'unica via d'uscita quando l'unita'
  // resta muta (vedi il pulsante "Riprova" piu' sotto). Incrementarlo e'
  // l'unico scopo del pulsante: entra fra le dipendenze dell'effetto sotto,
  // cosi' un retry rimette il vincolo in attesa (nuova finestra di
  // `ATTESA_VINCOLO_MS`) esattamente come un cambio lezione, e diventa la
  // `key` dell'iframe qualche riga piu' sotto, cosi' React lo smonta e lo
  // rimonta da capo invece di riusare lo stesso documento fallito — un
  // semplice "aggiorna" non l'avrebbe forzato, cambiare `key` si'.
  const [tentativoRicarica, setTentativoRicarica] = useState(0);

  // Letta dentro il timeout, non fra le dipendenze: se cambiasse mentre il
  // vincolo e' gia' noto rimetterebbe tutto in attesa a lezione avviata.
  const propedeuticitaRef = useRef(propedeuticita);
  useEffect(() => { propedeuticitaRef.current = propedeuticita; }, [propedeuticita]);

  useEffect(() => {
    // Ogni unita' riparte da zero: cambiando lezione il vincolo va ricalcolato.
    // Lo stesso reset serve al retry manuale (`tentativoRicarica` fra le
    // dipendenze): e' voluto che "Riprova" tratti l'unita' come appena
    // aperta, non come se avesse gia' fallito una volta.
    setGate(GATE_INIZIALE);
    setAvanzamentoMancato(false);
    setContenutoPronto(false);
    setUnitaHaChiesto(false);
    // NB: `altezzaContenuto` NON si azzera qui. Farlo vuol dire far collassare
    // il riquadro a 480px (il ripiego) a ogni cambio lezione, prima ancora che
    // il nuovo iframe abbia cominciato a caricare — un salto di misura secco,
    // che è proprio lo sfarfallio lamentato. Si tiene l'ultima altezza nota
    // come pavimento finché la lezione nuova non annuncia la propria (vedi
    // `igea:unit-height` più sotto); nel peggiore dei casi (unità che non
    // annuncia mai) il riquadro resta più alto del necessario, che è un
    // compromesso preferibile al collasso visibile.

    const attesa = setTimeout(() => {
      setGate((precedente) => (precedente.stato !== 'attesa' ? precedente : {
        ...precedente,
        stato: 'muta',
        // Fuori dal percorso ECM si apre lo stesso: li' non c'e' niente da
        // dimostrare, e un difetto nostro non deve fermare chi ha pagato il
        // corso. Dentro l'ECM resta chiuso e si dice perche'.
        aperto: !propedeuticitaRef.current,
      }));
    }, ATTESA_VINCOLO_MS);
    return () => clearTimeout(attesa);
  }, [unitId, tentativoRicarica]);

  // "Riprova": il fail-closed resta (niente scorciatoia per avanzare — il
  // vincolo torna `attesa`, non `aperto`), ma smette di essere un vicolo
  // cieco. Prima, quando l'unita' non si annunciava, l'unica indicazione era
  // testuale ("ricarica la pagina"): un'azione che l'utente doveva
  // reinventarsi (F5, tasto del browser), fuori da qualunque cosa il player
  // gli mostri. Un pulsante dentro il player la rende un'azione del prodotto,
  // non un rimedio improvvisato.
  const handleRiprova = () => setTentativoRicarica((n) => n + 1);

  // Dove sei arrivato. La ripresa "da dove avevo lasciato" non puo' appoggiarsi
  // a quella del MFE, che sceglie l'ultima BlockCompletion per data: per gli
  // utenti migrati le completion sono tutte congelate alla data dell'import, e
  // un blocco aperto oggi risulta modificato il giorno dell'import. La
  // posizione ce la teniamo noi, sull'iscrizione — ma non la scrive il player:
  // qui non c'e' il token dell'utente IGEA. La annunciamo al frontend Nuxt che
  // ospita l'iframe, che e' l'unico che puo' arrivare al backend.
  //
  // I nomi dei campi sono `corso`/`lezione`/`unita`, come tutto il resto del
  // vocabolario postMessage di IGEA (`igea:unit-height` usa `altezza`,
  // `igea:unit-gate` usa `vincolato` e `secondiMancanti`). Qui prima uscivano
  // `sequenceId`/`unitId` mentre il frontend Nuxt che ospita l'iframe legge
  // `unita`: i due lati non si incontravano, ed e' il motivo per cui la ripresa
  // non funzionava. `lezione` e' il sequential, `unita` la vertical.
  const posizioneAnnunciata = useRef(null);
  useEffect(() => {
    if (!courseId || !unitId || window.parent === window) { return; }
    // L'unita' puo' passare per `null` mentre si cambia lezione: l'uscita qui
    // sopra non azzera il riferimento, cosi' tornando sulla stessa unita' la
    // posizione non riparte una seconda volta.
    const chiave = `${sequenceId || ''}|${unitId}`;
    if (posizioneAnnunciata.current === chiave) { return; }
    posizioneAnnunciata.current = chiave;
    window.parent.postMessage({
      type: 'igea:posizione', corso: courseId, lezione: sequenceId, unita: unitId,
    }, '*');
  }, [courseId, sequenceId, unitId]);

  // Il messaggio arriva davvero dall'unita' che abbiamo caricato noi?
  //
  // Per `igea:quiz-unit` e `igea:attivita` la domanda non si pone: sono
  // campanelli senza dati, e chi li suonasse a sproposito otterrebbe di
  // aprire un test o di dichiarare presenza. `igea:materiale-richiesto`
  // invece PORTA un dato, e quel dato decide che cosa il tratto successivo
  // chiedera' al backend: accettarlo da chiunque significherebbe lasciar
  // scegliere a un mittente qualsiasi quale materiale far coniare.
  //
  // Questo e' anche l'unico punto della catena in cui la verifica e'
  // possibile: l'iframe dell'unita' lo creiamo noi e sappiamo dove punta,
  // quindi possiamo pretendere che il messaggio venga da QUELLA finestra e
  // da quell'origine. Piu' avanti (app IGEA) il messaggio arriva rilanciato
  // e l'origine non dice piu' niente sull'unita'.
  useEffect(() => {
    const dallUnita = (event) => {
      const finestra = iframeRef.current?.contentWindow;
      if (!finestra || event?.source !== finestra) { return false; }
      try {
        return event.origin === new URL(getConfig().LMS_BASE_URL).origin;
      } catch {
        return false;
      }
    };

    // L'esito torna all'unita', dove sta la scheda che aspetta. Si ricompone
    // campo per campo invece di rigirare il messaggio ricevuto: cosi' il
    // link firmato — che qui non deve nemmeno passare, e infatti non passa —
    // non puo' finire in un documento di un'altra origine per distrazione di
    // chi un giorno aggiungera' un campo dall'altra parte.
    const rispondiAllUnita = (allegato, esito, motivo) => {
      const finestra = iframeRef.current?.contentWindow;
      if (!finestra) { return; }
      finestra.postMessage({
        type: 'igea:materiale-esito', allegato, esito, motivo,
      }, '*');
    };

    const onMessage = (event) => {
      const dato = event?.data;
      if (dato?.type === 'igea:unit-height' && Number.isFinite(dato.altezza)) {
        // Il tetto evita che un'unita' con contenuti infiniti (trascrizioni,
        // liste) faccia crescere l'iframe fuori da ogni misura ragionevole.
        setAltezzaContenuto(Math.min(Math.max(dato.altezza, 480), 4000));
      }
      // L'unita' chiede quanto spazio ha. E' l'altra meta' di
      // `igea:content-viewport`: vedi il commento sull'effetto che risponde.
      if (dato?.type === 'igea:content-viewport-richiesta') {
        setUnitaHaChiesto(true);
      }
      if (dato?.type === 'igea:unit-gate') {
        setGate({
          stato: 'noto',
          vincolato: !!dato.vincolato,
          aperto: !!dato.aperto,
          secondiMancanti: Number(dato.secondiMancanti) || 0,
          tipo: dato.tipo === 'video' || dato.tipo === 'testo' ? dato.tipo : '',
          percorsoVincolato: !!dato.percorsoVincolato,
        });
      }
      if (dato?.type === 'igea:advance-timeout') {
        setAvanzamentoMancato(true);
      }
      // L'unita' del test ECM contiene solo un segnaposto: il test lo disegna
      // il frontend IGEA, che e' l'unico ad avere il token dell'utente per
      // chiamare le API. Qui si fa da ponte fra i due iframe — l'annuncio
      // nasce nell'unita' (lms.*), passa da questo player (learning.*) e
      // arriva all'app (app.*), che sono tre origini diverse.
      if (dato?.type === 'igea:quiz-unit' && window.parent !== window) {
        window.parent.postMessage({ type: 'igea:quiz-unit' }, '*');
      }
      // Stesso ponte, per la presenza. Il tempo di fruizione ECM lo conta
      // l'app che ci ospita, e per sapere se davanti allo schermo c'e'
      // qualcuno guardava i gesti sul proprio documento — che pero' l'utente
      // non tocca mai, perche' questo player lo copre per intero e sta su
      // un'altra origine. L'annuncio nasce dentro l'unita', dove i gesti si
      // vedono, e senza questo rilancio si fermerebbe qui. E' gia' limitato
      // alla sorgente: quello che passa e' un segno di presenza, non un tic a
      // intervallo fisso, e l'app rifiuta comunque i messaggi periodici.
      if (dato?.type === 'igea:attivita' && window.parent !== window) {
        window.parent.postMessage({ type: 'igea:attivita' }, '*');
      }
      // Materiale didattico (DEC-064). Gli allegati stanno su S3, in un
      // bucket privato, e si scaricano solo con un link firmato che il
      // backend emette dopo aver verificato l'iscrizione: il campo
      // `resources` della lezione porta un codice, non un indirizzo.
      //
      // Chi puo' chiedere quel link non e' ne' l'unita' (lms.*, nessun token
      // IGEA) ne' questo player (learning.*, nessun token IGEA e nessun id
      // IGEA del corso: qui il corso e' `course-v1:…`, mentre l'endpoint lega
      // `{course}` alla riga di `courses`). E' l'app che ci ospita, dove il
      // token vive in memoria (DEC-048) e dove il corso e' gia' noto. Quindi
      // la richiesta risale un tratto ancora, come `igea:quiz-unit`.
      //
      // Il corso NON viaggia nel messaggio, di proposito: chi ospita
      // l'iframe sa gia' di quale corso si tratta, e prenderlo da un
      // postMessage vorrebbe dire far scegliere a un mittente non
      // verificabile su quale iscrizione chiedere il materiale. E' la stessa
      // regola che `leggiPosizione` applica alla posizione di ripresa.
      if (dato?.type === 'igea:materiale-richiesto' && dallUnita(event)) {
        const { allegato } = dato;
        if (typeof allegato !== 'string') { return; }
        if (!FORMA_ALLEGATO.test(allegato)) {
          // Una voce mai riscritta da `rewrite_resource_codes` (l'id e' il
          // nome del file). Non c'e' niente da chiedere: si risponde subito
          // invece di lasciare la scheda in attesa fino alla scadenza.
          rispondiAllUnita(allegato, 'errore', 'non-trovato');
          return;
        }
        if (window.parent === window) {
          // Player aperto fuori dall'app IGEA: nessuno a cui chiedere.
          rispondiAllUnita(allegato, 'errore', 'non-disponibile');
          return;
        }
        window.parent.postMessage({ type: 'igea:materiale-richiesto', allegato }, '*');
      }
      // E l'esito torna giu'. Solo dall'app che ci ospita: un esito da
      // un'altra finestra potrebbe spegnere l'attesa di un download vero, o
      // scrivere «non risulti iscritto» dentro una lezione pagata.
      if (dato?.type === 'igea:materiale-esito'
          && window.parent !== window && event.source === window.parent) {
        rispondiAllUnita(
          dato.allegato,
          dato.esito === 'ok' ? 'ok' : 'errore',
          typeof dato.motivo === 'string' ? dato.motivo : undefined,
        );
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const handleLoad = () => {
    // Segnale che il nuovo documento e' arrivato: l'overlay puo' sparire.
    // Vale anche cross-origin — il `try/catch` sotto riguarda solo
    // l'iniezione del CSS, non la visibilita' del contenuto.
    setContenutoPronto(true);
    const iframe = iframeRef.current;
    if (!iframe) { return; }
    try {
      const doc = iframe.contentDocument;
      if (!doc) { return; }
      if (!doc.getElementById('igea-iframe-injected')) {
        const style = doc.createElement('style');
        style.id = 'igea-iframe-injected';
        style.textContent = IFRAME_INJECT_CSS;
        doc.head.appendChild(style);
      }
    } catch {
      // Cross-origin — non-fatal, just skip the injection.
    }
  };

  // Contratto nuovo verso l'unita': `igea:content-viewport`, player -> unita'
  // (direzione opposta a tutti gli altri messaggi di questo file, che vanno
  // dall'unita' verso il player). Campo `altezza` in px, stesso nome usato da
  // `igea:unit-height` per coerenza col resto del vocabolario. E' quanto
  // spazio verticale il player ha davvero a disposizione per il contenuto,
  // dopo aver tolto header e footer — non e' un limite arbitrario, e' la
  // stessa misura che il player userebbe per se stesso.
  //
  // Mandato solo quando l'iframe e' pronto (altrimenti nessuno script del
  // documento nuovo e' ancora in ascolto: un postMessage senza destinatario
  // attivo si perde, non viene messo in coda) e solo quando la misura e'
  // nota. Si ripete ogni volta che l'una o l'altra cambiano: `contenutoPronto`
  // passa a `true` a ogni nuova lezione (nuovo documento, va informato da
  // capo), `alturaDisponibile` cambia quando la finestra viene ridimensionata
  // (lo stesso ResizeObserver che la misura). NON dipende da
  // `altezzaContenuto`: quello e' cio' che l'unita' dice a noi, non il
  // contrario, e farlo dipendere da li' avrebbe creato un giro di richiami
  // fra le due parti invece di un'informazione a senso unico.
  //
  // Aspettare `load` pero' NON basta, ed e' il difetto vero dietro alla
  // segnalazione «il video e' troppo grande e nasconde i comandi e i tab»
  // (bug-101, misurato sul campo il 2026-08-12 con una spia installata prima
  // degli script di pagina: al primo caricamento gli annunci RICEVUTI
  // dall'unita' erano zero, e il primo arrivava solo ridimensionando la
  // finestra). Il listener dell'unita' lo registra lo script dell'XBlock, che
  // parte quando l'XBlock runtime lo inizializza — dopo che `load` e' gia'
  // scattato qui. L'annuncio partiva verso un documento in cui nessuno stava
  // ancora ascoltando, e un postMessage senza destinatario attivo si perde:
  // nessun limite scritto, video a piena larghezza.
  //
  // La correzione e' simmetrica: chi ha bisogno del dato lo CHIEDE quando e'
  // pronto (`igea:content-viewport-richiesta`, unita' -> player, stessa
  // convenzione degli altri messaggi dell'unita') e chi ce l'ha risponde.
  // `unitaHaChiesto` entra fra le dipendenze proprio per questo: e' il
  // segnale che rimanda l'annuncio anche quando `load` era gia' passato.
  // Vale in entrambi i sensi — se e' l'unita' ad arrivare per prima e la
  // misura non c'e' ancora, la richiesta non resta appesa: l'annuncio parte
  // da se' appena `alturaDisponibile` e' nota. E se ne partono due (la
  // risposta e l'annuncio spontaneo) portano lo stesso numero, quindi
  // l'unita' riscrive lo stesso limite e niente balla.
  useEffect(() => {
    if (!contenutoPronto && !unitaHaChiesto) { return; }
    if (!alturaDisponibile) { return; }
    const finestra = iframeRef.current?.contentWindow;
    if (!finestra) { return; }
    finestra.postMessage({ type: 'igea:content-viewport', altezza: alturaDisponibile }, '*');
  }, [contenutoPronto, unitaHaChiesto, alturaDisponibile]);

  useEffect(() => {
    if (iframeRef.current?.contentDocument?.readyState === 'complete') {
      handleLoad();
    }
  }, [unitId]);

  // `tw-no-underline` + il colore ribadito in hover servono contro gli stili
  // link di Bootstrap: i pulsanti di navigazione sono <a>, e senza questo al
  // passaggio del mouse comparivano sottolineatura e colore blu di default.
  const navBtnBase = 'tw-inline-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs md:tw-gap-2 md:tw-px-4 md:tw-py-2 md:tw-text-sm tw-rounded-igea tw-font-medium tw-transition-colors tw-no-underline hover:tw-no-underline';
  const enabledBtn = `${navBtnBase} tw-bg-igea-bg hover:tw-bg-gray-100 tw-text-text hover:tw-text-text`;
  // NB: i token sono `igea-green` / `igea-green-dark` (vedi tailwind.config.js).
  // Qui c'era `igea-primary`, che nella palette non esiste: Tailwind non
  // generava la classe, il bottone restava senza sfondo e il testo bianco
  // spariva sulla card bianca. Si vedeva solo al passaggio del mouse, perché
  // lì interveniva il colore di default dei link di Bootstrap.
  const primaryBtn = `${navBtnBase} tw-bg-igea-green hover:tw-bg-igea-green-dark tw-text-white hover:tw-text-white`;
  const disabledBtn = `${navBtnBase} tw-bg-gray-50 tw-text-gray-300 tw-cursor-not-allowed`;

  // Cosa manca per proseguire, detto in modo che si capisca: un conto alla
  // rovescia per i testi, l'invito a finire il video per i video, e — quando
  // l'unita' non si e' fatta sentire — il motivo per cui non si va avanti,
  // invece di un pulsante spento senza spiegazione.
  const { etichettaVincolo, spiegazioneVincolo } = (() => {
    if (gate.aperto) { return { etichettaVincolo: '', spiegazioneVincolo: '' }; }
    if (gate.stato === 'attesa') {
      return {
        etichettaVincolo: '· verifica in corso',
        spiegazioneVincolo: 'Sto verificando i requisiti di questa lezione.',
      };
    }
    if (gate.stato === 'muta') {
      return {
        etichettaVincolo: '· non risponde',
        spiegazioneVincolo: 'Questa lezione non ha comunicato i suoi requisiti. '
          + 'Premi «Riprova»: nel percorso ECM non si prosegue senza averli verificati.',
      };
    }
    if (!gate.vincolato) { return { etichettaVincolo: '', spiegazioneVincolo: '' }; }
    // Pagina di blocco della propedeuticita' (collaudo P-05, learning #7): la
    // lezione non si e' nemmeno aperta, quindi non c'e' un video da finire ne'
    // un tempo che scorre — manca una lezione precedente.
    if (gate.percorsoVincolato) {
      return {
        etichettaVincolo: '· lezione bloccata',
        spiegazioneVincolo: 'Completa prima le lezioni precedenti.',
      };
    }
    if (gate.secondiMancanti > 0) {
      const min = Math.floor(gate.secondiMancanti / 60);
      const sec = gate.secondiMancanti % 60;
      return {
        etichettaVincolo: min > 0 ? `· ${min}:${String(sec).padStart(2, '0')}` : `· ${sec}s`,
        spiegazioneVincolo: 'Tempo minimo di lettura non ancora trascorso.',
      };
    }
    if (gate.tipo === 'video') {
      return {
        etichettaVincolo: '· completa il video',
        spiegazioneVincolo: 'Guarda il video fino alla soglia richiesta per proseguire.',
      };
    }
    return {
      etichettaVincolo: '· completa la lezione',
      spiegazioneVincolo: 'Completa questa lezione per proseguire.',
    };
  })();

  // Stessa altezza per overlay/placeholder: cosi' il riquadro non cambia
  // misura quando l'uno si scambia con l'altro durante il cambio lezione —
  // e' il pezzo che tiene fede al commento su `altezzaContenuto` qui sopra.
  const alturaContenitore = altezzaContenuto ? `${altezzaContenuto}px` : '480px';

  // Segnalazione cliente (2026-08-11), due giri sullo stesso problema:
  // 1. "lezione con video, non riesco a scrollare in basso, il player e'
  //    tagliato e non c'e' il bottone per avanzare" — causa reale, non il
  //    rischio strutturale dell'overlay (quello resta confinato al riquadro
  //    video, non arriva mai al footer): l'iframe portava un'altezza
  //    ESPLICITA uguale a quella annunciata dall'unita', fino a 4000px. Ne'
  //    questo `<main>` ne' il layout che lo contiene (`IgeaPlayerLayout`,
  //    `tw-h-screen ... tw-overflow-hidden`) scorrono: sono bloccati
  //    all'altezza dello schermo, quindi tutto cio' che restava fuori —
  //    footer coi pulsanti Avanti/Indietro compreso — veniva tagliato via,
  //    non spostato piu' in basso.
  // 2. Correzione della correzione: "l'ideale sarebbe adattare la dimensione
  //    del video allo schermo in modo da non vederlo mai tagliato" — non
  //    scorrere un contenuto troppo alto, farlo STARE nello spazio
  //    disponibile. Chi guarda un video non dovrebbe scorrere per vederlo
  //    ne' per raggiungere il pulsante dopo.
  //
  // Il video non lo disegna questo file: e' dentro l'iframe (XBlock
  // `igea_video`, altro repo). Qui si puo' solo NON lasciar crescere
  // l'iframe oltre lo spazio misurato (`alturaDisponibile`, dal
  // ResizeObserver qui sopra) e AVVISARE l'unita' di quanto spazio ha,
  // cosi' un domani potra' restringere il video da sola (vedi
  // `igea:content-viewport` qui sopra). Finche' quel lato non e'
  // implementato, il tetto qui sotto e' comunque cio' che garantisce che il
  // footer resti raggiungibile senza scorrere: se il video non si restringe
  // da solo, e' l'iframe a mostrare la propria barra di scorrimento interna
  // (comportamento nativo, nessun CSS speciale) — mai il player a crescere
  // oltre lo schermo. `tw-overflow-y-auto` sul riquadro resta come rete di
  // sicurezza per la finestra di misurazione prima che `alturaDisponibile`
  // sia nota (il primo render, o un browser senza ResizeObserver), non come
  // meccanismo primario.
  //
  // SOLO quando c'e' un iframe vivo il riquadro smette di imporre la propria
  // altezza al layout (niente piu' `style.height` qui) e diventa un elemento
  // flessibile (`tw-flex-1 tw-min-h-0`) che occupa quel che resta di
  // `<main>` dopo il footer — e' proprio questa la scatola che si misura.
  // Il footer resta un fratello normale FUORI dall'area del contenuto: non
  // e' "fissato" con CSS apposta (`position: sticky`), lo e' di conseguenza,
  // perche' non condivide piu' lo spazio con un contenuto che puo' crescere
  // senza limite — e' sempre l'ultima cosa disegnata in `<main>`, mai
  // spinta fuori, mai da raggiungere scorrendo. Il placeholder (nessun
  // iframe, transizione fra lezioni) resta con l'altezza esplicita di
  // prima: e' un testo breve, non rischia di superare lo spazio disponibile,
  // e cambiare quel ramo avrebbe rotto la garanzia anti-sfarfallio del fix
  // precedente (97ccf67) senza bisogno.
  //
  // Segnalazione cliente (2026-08-12): «se scrollo il video c'e' qualcosa che
  // lo fa riscattare in alto... e' fastidioso». Qui c'era
  // `Math.min(altezzaContenuto, alturaDisponibile)`, cioe' il riquadro
  // INSEGUIVA l'altezza del contenuto ogni volta che ci stava. E' quello il
  // rimbalzo, ed e' meccanico: quando un iframe diventa alto quanto il proprio
  // documento non resta piu' niente da scorrere e il browser riporta a zero la
  // posizione interna. Misurato su Chrome — documento 1120, riquadro 700,
  // utente a 60; portato il riquadro a 1120 la posizione torna a 0; tenuto il
  // riquadro fermo a 747 la posizione resta 60.
  //
  // Bastava un istante di contenuto piu' alto del riquadro (una scheda che si
  // apre, la trascrizione che arriva, il video che si ridimensiona dopo
  // `igea:content-viewport`) perche' l'utente potesse scorrere; l'annuncio
  // successivo — e l'unita' ne manda uno a ogni ridimensionamento del proprio
  // body, vedi il ResizeObserver in `plugins/igea/unit_gate.py` — rimetteva il
  // riquadro alla misura del contenuto e cancellava lo scorrimento appena
  // iniziato. Il difetto e' nato con l'adattamento del video: da quando
  // l'XBlock rimpicciolisce il player perche' la lezione STIA nello spazio,
  // il contenuto e' quasi sempre piu' basso dello spazio, cioe' proprio il
  // ramo in cui il minimo faceva inseguire il contenuto.
  //
  // Ora il riquadro prende TUTTO lo spazio misurato e non si muove piu': la
  // sua altezza non dipende piu' da cio' che l'unita' annuncia, quindi gli
  // annunci non possono piu' spostare niente (l'anello e' tagliato alla
  // radice, non ritardato). Il dimensionamento non cambia: il video si adatta
  // perche' l'XBlock legge `igea:content-viewport`, che porta lo stesso numero
  // — `alturaDisponibile` — e l'iframe continua a non superare mai lo spazio,
  // quindi il footer resta raggiungibile senza scorrere. Se il contenuto ci
  // sta, l'iframe e' solo piu' alto del necessario e non si vede differenza
  // (sotto c'e' il bianco della card); se non ci sta, scorre con una barra
  // stabile che nessuno azzera piu'.
  const alturaIframe = alturaDisponibile ? `${alturaDisponibile}px` : alturaContenitore;
  const areaContenutoClassName = `tw-relative tw-w-full tw-overflow-x-hidden${
    iframeUrl ? ' tw-flex-1 tw-min-h-0 tw-overflow-y-auto' : ''
  }`;
  const areaContenutoStyle = iframeUrl ? undefined : { height: alturaContenitore };

  return (
    <main
      className="tw-flex-1 tw-min-w-0 tw-min-h-0 tw-bg-white tw-rounded-igea tw-overflow-hidden tw-flex tw-flex-col tw-shadow-igea-card"
    >
      <div ref={areaContenutoRef} className={areaContenutoClassName} style={areaContenutoStyle}>
        {iframeUrl && (
          <iframe
            // La `key` include `tentativoRicarica`: e' quello che rende
            // "Riprova" un vero rimontaggio invece di un semplice
            // ridisegno. React smonta l'iframe vecchio e ne monta uno
            // nuovo con `src` identico, che e' comunque una richiesta
            // fresca al server — non riusa il documento fallito.
            key={`${unitId}-${tentativoRicarica}`}
            ref={iframeRef}
            src={iframeUrl}
            title={unit?.title || 'Course content'}
            className="tw-w-full tw-border-0 tw-block"
            style={{ height: alturaIframe }}
            allow="autoplay; fullscreen; clipboard-read; clipboard-write"
            onLoad={handleLoad}
          />
        )}
        {/* Overlay di transizione: copre il riquadro finche' non c'e' ancora
            un documento pronto da mostrare — sia perche' l'unita' manca del
            tutto (`unitId` nullo durante il cambio lezione), sia perche'
            l'iframe nuovo non ha ancora finito di caricare. Sostituisce il
            bianco che si vedeva prima durante la navigazione dell'iframe.
            Resta confinato a questo div anche ora che puo' scorrere: essendo
            posizionato in modo assoluto rispetto al riquadro (non al suo
            contenuto), copre solo la parte visibile — mai il footer, che e'
            un fratello fuori da qui. */}
        {(!iframeUrl || !contenutoPronto) && (
          <div
            className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-text-text-muted tw-text-sm"
          >
            Caricamento contenuto…
          </div>
        )}
      </div>
      {/* Lesson navigation footer — Avanti / Indietro tra lezioni.
          Centered + flex-wrap so the buttons remain visible on narrow
          viewports (and inside the IGEA frontend modal which can drop
          below 1280px). */}
      <div className="tw-border-t tw-border-gray-200 tw-bg-white tw-px-2 tw-py-2 md:tw-px-4 md:tw-py-3 tw-flex tw-flex-nowrap tw-items-center tw-justify-center tw-gap-2 md:tw-gap-3">
        {prevLesson ? (
          <Link to={prevLesson} className={enabledBtn}>
            <span aria-hidden="true">←</span>
            <span>Lezione precedente</span>
          </Link>
        ) : (
          <span className={disabledBtn} aria-disabled="true">
            <span aria-hidden="true">←</span>
            <span>Lezione precedente</span>
          </span>
        )}
        {nextLesson && gate.aperto ? (
          <Link to={nextLesson} className={primaryBtn}>
            <span>Lezione successiva</span>
            <span aria-hidden="true">→</span>
          </Link>
        ) : (
          <span className={disabledBtn} aria-disabled="true" title={spiegazioneVincolo}>
            <span>Lezione successiva</span>
            {etichettaVincolo ? (
              <span className="tw-font-semibold">{etichettaVincolo}</span>
            ) : (
              <span aria-hidden="true">→</span>
            )}
          </span>
        )}
        {/* Via d'uscita quando l'unita' resta muta: SOLO in quel caso, e SOLO
            quando il vincolo e' ancora chiuso (`!gate.aperto`, cioe' siamo
            davvero nel ramo ECM — fuori dall'ECM la muta apre lo stesso e
            non c'e' niente da riprovare). Non e' una scorciatoia per
            avanzare: rimette il vincolo in `attesa`, non in `aperto`. */}
        {gate.stato === 'muta' && !gate.aperto && (
          <button type="button" onClick={handleRiprova} className={enabledBtn}>
            <span aria-hidden="true">↻</span>
            <span>Riprova</span>
          </button>
        )}
      </div>
      {avanzamentoMancato && (
        <p role="status" className="tw-bg-white tw-px-4 tw-pb-3 tw-text-center tw-text-sm tw-text-text-muted">
          {AVVISO_AVANZAMENTO_MANCATO}
        </p>
      )}
    </main>
  );
};

IgeaMainContent.propTypes = {
  courseId: PropTypes.string,
  sequenceId: PropTypes.string,
  unitId: PropTypes.string,
  // Se in questo corso c'e' propedeuticita'. Decide da che parte si sbaglia
  // quando l'unita' non risponde: aperto fuori dall'ECM, chiuso dentro.
  propedeuticita: PropTypes.bool,
  // Elenco ordinato delle lezioni del corso (dall'outline), calcolato da
  // IgeaPlayerLayout. Vuoto finche' l'outline non e' ancora arrivato: in
  // quel caso prev/next restano entrambi null, e il pulsante lo dice invece
  // di sparire (vedi il render piu' sotto).
  sequenceIds: PropTypes.arrayOf(PropTypes.string),
};

IgeaMainContent.defaultProps = {
  courseId: null,
  sequenceId: null,
  unitId: null,
  propedeuticita: false,
  sequenceIds: [],
};

export default IgeaMainContent;
